import {
  Button,
  Grid,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import CancelIcon from '@material-ui/icons/Cancel';
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useRoles } from "../../../hooks/useRoles";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { deleteRole } from "../../../services/roles.service";
import DialogCustom from "../../Common/DialogCustom";
import { Snackbar } from "../../Common/Snackbar";
import { checkAccess } from "../utils/index";
import Header from "./Info";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw"
  },
  cancelIcon: {
    color: theme.palette.error.main
  },
  iconText: {
    ...theme.typography.subtitle1,
    cursor: "pointer"
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  buttonOutlined: {
    border: "2px solid",
    ...theme.typography.subtitle1,
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  spacer: {
    marginBottom: 20
  }
}));

const RolePage = ({
  t,
  history,
  match,
  actionShowSpinner,
}) => {
  const { userAccess } = useAuth();
  const classes = useStyles();
  const { params: { id } } = match;
  const {
    getRole,
    role,
    setDeleteRole,
    success,
    error,
    create,
    update,
  } = useRoles({
    spinner: actionShowSpinner,
  });
  const [message, setMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [dataApiRequest, setDataApiRequest] = useState({});

  const [canCreateGeneralRule, setCanCreateGeneralRule] = useState({});
  const [canDeleteGeneralRule, setCanDeleteGeneralRule] = useState({});
  const [canUpdateGeneralRule, setCanUpdateGeneralRule] = useState({});

  useEffect(() => {
    if (userAccess) {
      const hasAccessToCreateGeneralRule = checkAccess(userAccess, 'createGeneralRule');
      const hasAccessToDeleteGeneralRule = checkAccess(userAccess, 'deleteGeneralRule');
      const hasAccessToUpdateGeneralRule = checkAccess(userAccess, 'updateGeneralRule');

      setCanCreateGeneralRule(hasAccessToCreateGeneralRule);
      setCanDeleteGeneralRule(hasAccessToDeleteGeneralRule);
      setCanUpdateGeneralRule(hasAccessToUpdateGeneralRule);

      if (id) getRole(id);
    }
  }, [id, userAccess]);

  const handleChangeData = (data) => {
    setDataApiRequest({ ...dataApiRequest, ...data });
  };

  useEffect(() => {
    if (success.disable === "disable") {
      setMessage({
        open: true,
        content: "Status alterado com sucesso!",
        type: "success",
      });
    }
    if (success.create === "create") {
      setTimeout(function () {
        history.push({ pathname: "/roles", state: { successCreated: true } });
      });
    }
    if (success.update === "update") {
      setTimeout(function () {
        history.push({ pathname: "/roles", state: { successUpdated: true } });
      });
    }
  }, [success]);

  useEffect(() => {
    if (error.create) {
      setMessage({
        open: true,
        content: error.create,
        type: "error",
        autoHideDuration: null,
      });
    }
    if (error.update) {
      setMessage({
        open: true,
        content: error.update,
        type: "error",
        autoHideDuration: null,
      });
    }
  }, [error]);

  const handleSave = async () => {
    actionShowSpinner(true);

    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });

    const request = {
      name: dataApiRequest.nameRole,
      description: dataApiRequest.descriptionRole,
    };

    if (!id) {
      await create(request);
    } else {
      await update(id, request);
    }

    actionShowSpinner(false);
  };

  const handleDelete = async () => {
    const response = await deleteRole(id);
    setOpen(false);

    if(response.error) {
      setMessage({
        open: true,
        content: response.error.length > 1 ? response.error : t("forms.role.errorDelete"),
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    history.push({ pathname: "/roles", state: { successDeleted: true } });
  };

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid container className={classes.root}>
      <Grid container item direction="row" justify="space-between" className={classes.spacer}>
        <Grid item md={6} lg={6}>
          <Typography variant="h4">
            {t(`forms.role.${id ? "edit" : "new"}`)}
          </Typography>
        </Grid>
        {
          id && (
            <Grid container item direction="row" md={6} lg={6} alignItems="center" justify="center">
              <IconButton disabled={!canDeleteGeneralRule} onClick={handleClickOpen}>
                <CancelIcon className={classes.cancelIcon} />
              </IconButton>
              <Typography className={classes.iconText} onClick={canDeleteGeneralRule ? () => handleClickOpen() : () => console.log('no access')}>
                {t(`forms.dialog.delete`)}
              </Typography>
            </Grid>
          )
        }
        <div>
          <DialogCustom
            open={open}
            handleClose={handleClose}
            handleChange={handleDelete}
            title={t(`forms.dialog.warning`)}
            body={t("forms.dialog.textDeleteRole")}
            confirmText={t(`forms.dialog.yes`)}
            cancelText={t(`forms.dialog.no`)}
            error={true}
          />
        </div>
      </Grid>
      <Grid container item direction="row" className={classes.spacer}>
        <Grid item md={12} lg={12}>
          <Header
            onChangeData={handleChangeData}
            spinner={actionShowSpinner}
            role={role}
          />
        </Grid>
      </Grid>
      <Grid container item direction="row" justify="center" spacing={2} >
        <Grid item>
          <Button
            variant="outlined"
            className={classes.buttonOutlined}
            onClick={() => history.push("/roles")}
          >
            {t("cancel")}
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            className={classes.button}
            onClick={handleSave}
            disabled={(!id && !canCreateGeneralRule) || (id && !canUpdateGeneralRule)}
          >
            {t("addRole")}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        message={message.content}
        type={message.type}
        open={message.open}
        autoHideDuration={message.autoHideDuration}
      />
    </Grid>
  );
};

const withRouterRolePage = withRouter(RolePage);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const RolePageConnect = connect(
  null,
  mapDispatchToProps
)(withRouterRolePage);

export default withTranslation()(RolePageConnect);
